<script setup>
import { ref, computed } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import ModalHeader from 'dashboard/components/ModalHeader.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import { useAlert } from 'dashboard/composables';

const props = defineProps({
  show: {
    type: Boolean,
    default: false,
  },
  currentChat: {
    type: Object,
    required: true,
  },
});

const emit = defineEmits(['cancel']);

const store = useStore();
const { t } = useI18n();

const isRequesting = ref(false);
const templateName = ref('request_call_permission'); // Default name as required by Meta

const currentContact = computed(() =>
  store.getters['contacts/getContact'](props.currentChat.meta.sender.id)
);

const phone = computed(() => currentContact.value?.phone_number);

const canRequest = computed(() => !!phone.value && !!templateName.value);

const onClose = () => {
  emit('cancel');
};

const requestPermission = async () => {
  if (!canRequest.value) {
    useAlert(
      t('WHATSAPP_CALLING.ERRORS.NO_PHONE_NUMBER') ||
        'Contact has no phone number.'
    );
    return;
  }

  isRequesting.value = true;
  try {
    await store.dispatch('requestWhatsAppCallPermission', {
      contactId: currentContact.value.id,
      inboxId: props.currentChat.inbox_id,
      phone: phone.value,
      templateName: templateName.value,
      languageCode: 'en_US', // Meta standard or could be dynamic
    });
    useAlert(
      t('WHATSAPP_CALLING.PERMISSION_REQUEST_SUCCESS') ||
        'Permission request sent successfully.'
    );
    emit('cancel');
  } catch (error) {
    useAlert(
      error.message ||
        t('WHATSAPP_CALLING.ERRORS.FAILED') ||
        'Failed to send permission request.'
    );
  } finally {
    isRequesting.value = false;
  }
};
</script>

<template>
  <woot-modal :show="show" :on-close="onClose">
    <div class="flex flex-col h-auto overflow-auto w-[500px]">
      <ModalHeader
        :header-title="
          t('WHATSAPP_CALLING.PERMISSION_MODAL.TITLE') ||
          'Request Call Permission'
        "
        :header-content="
          t('WHATSAPP_CALLING.PERMISSION_MODAL.DESCRIPTION') ||
          'Send a WhatsApp template message requesting permission to call this contact.'
        "
      />
      <form
        class="flex flex-col w-full px-8 pt-6 pb-8"
        @submit.prevent="requestPermission"
      >
        <label class="w-full">
          <span class="block mb-2 text-sm font-medium text-slate-700">
            {{
              t('WHATSAPP_CALLING.PERMISSION_MODAL.TEMPLATE_NAME') ||
              'Template Name'
            }}
          </span>
          <input
            v-model="templateName"
            type="text"
            class="w-full px-3 py-2 border rounded-md border-slate-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
            :placeholder="
              t('WHATSAPP_CALLING.PERMISSION_MODAL.PLACEHOLDER') ||
              'e.g. request_call_permission'
            "
            required
          />
        </label>

        <div class="flex justify-end gap-2 mt-6">
          <NextButton
            :label="t('WHATSAPP_CALLING.PERMISSION_MODAL.CANCEL') || 'Cancel'"
            variant="ghost"
            @click="onClose"
          />
          <NextButton
            :label="
              t('WHATSAPP_CALLING.PERMISSION_MODAL.SEND') || 'Send Request'
            "
            :is-loading="isRequesting"
            :disabled="!canRequest"
            @click="requestPermission"
          />
        </div>
      </form>
    </div>
  </woot-modal>
</template>
